import { CaretLeft, CaretRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars, elevationCss, colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useLocation, useRouteMatch, matchPath } from 'react-router';
import styled, { ThemeConsumer } from 'styled-components';

import { useTelemetryViewEvent } from 'common/hooks';
import { menuEditorView } from 'core/telemetry/avo/Avo';

import { MenuEditorDnDContext } from './MenuEditorDnDContext';
import { MenuFeaturesContext } from './MenuFeaturesContext';
import { MenuPermissionsContext } from './MenuPermissionsContext';
import { useMoveMenuItems } from './useMoveMenuItems';
import { MenuLocaleContext } from '../MenuLocaleContext';
import { MenuMainElementRefContext } from '../MenuMainElementRefContext';
import { MenuMetaContext } from '../MenuMetaContext';
import MenuNavigation from '../MenuNavigation';
import CreateCategoryFormContainer from '../categories/components/CreateCategoryForm';
import AllDisabled from '../items/components/AllDisabled';
import AllItems from '../items/components/AllItems';
import { CategoryItems, SubCategoryItems } from '../items/components/CategoryItems';
import ItemsWithNoCategory from '../items/components/ItemsWithNoCategory';
import { MenuSyncPage } from '../menu-sync';
import { MenuSyncContext } from '../menu-sync/MenuSyncContext';
import { useMenuSyncStatus } from '../menu-sync/useMenuSyncStatus';
import Options from '../options/components/Options';
import RestockingRulesPage from '../restocking-rules';
import { MenuPermissions, MenuFeatures } from '../types';
import useMenuData from '../useMenuData';

type Props = {
  country: Country;
  menuFeatures: MenuFeatures;
  menuId: string;
  menuPermissions: MenuPermissions;
  merchantId: string;
  primaryLanguage: Language;
  refetchMenu: () => void;
  timezone: string;
  venueId: string;
  venueSlug: string;
  woltcomUrl: string;
};

const COLLAPSE_TRANSITION_MS = 200;

const Root = styled.div`
  background: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex: 1;
  height: 0;
`;

const LeftNavigation = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const NavigationContainer = styled.div<{ collapsed: boolean }>`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  width: ${({ collapsed }) => (collapsed ? '24px' : '320px')};
  border-inline-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  transition: width ${COLLAPSE_TRANSITION_MS}ms;
  pointer-events: all;
  &::after {
    content: '';
    position: absolute;
    left: ${({ theme }) => (theme.cbDirection === 'ltr' ? 0 : 'auto')};
    right: ${({ theme }) => (theme.cbDirection === 'ltr' ? 'auto' : 0)};
    top: 0;
    background-color: ${cbSemanticCssVars.surfaceMain};
    opacity: ${({ collapsed }) => (collapsed ? 1 : 0)};
    width: ${({ collapsed }) => (collapsed ? '24px' : '320px')};
    height: 100%;
    pointer-events: ${({ collapsed }) => (collapsed ? 'all' : 'none')};
    transition: all ${COLLAPSE_TRANSITION_MS}ms;
    z-index: 1;
    border-inline-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  /* ignore-color */
  scrollbar-color: ${colors.pepper32} transparent;

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt36 })};
    border-radius: 20px;
    border: 3px solid ${cbSemanticCssVars.surfaceMain};
  }
`;

const Navigation = styled(MenuNavigation)``;

const CollapseButton = styled.div<{ collapsed: boolean }>`
  ${elevationCss(6)};
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  top: 32px;
  left: ${({ theme, collapsed }) => {
    if (theme.cbDirection !== 'ltr') {
      return 'auto';
    }
    return collapsed ? '12px' : '308px';
  }};
  right: ${({ theme, collapsed }) => {
    if (theme.cbDirection === 'ltr') {
      return 'auto';
    }
    return collapsed ? '12px' : '308px';
  }};
  margin-block-start: -12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all ${COLLAPSE_TRANSITION_MS}ms;
  z-index: 2;
  svg {
    width: 12px;
    fill: ${cbSemanticCssVars.textPrimary};
  }
  &:active svg {
    fill: ${cbSemanticCssVars.textSecondary};
  }
  &:hover {
    ${elevationCss(16)};
  }
`;

const Main = styled.div`
  flex: 1;
  overflow: auto;
`;

const CollapseIcon = ({ direction, collapsed }: { direction: 'ltr' | 'rtl'; collapsed: boolean }) => {
  if (direction === 'ltr') {
    return collapsed ? <CaretRight /> : <CaretLeft />;
  }
  return collapsed ? <CaretLeft /> : <CaretRight />;
};

const MenuEditor = ({
  country,
  menuFeatures,
  menuId,
  menuPermissions,
  merchantId,
  primaryLanguage,
  refetchMenu,
  timezone,
  venueId,
  venueSlug,
  woltcomUrl,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { path, url } = useRouteMatch();
  const { categories } = useMenuData();
  const { pathname } = useLocation();
  const mainElementRef = useRef<HTMLDivElement>(null);

  const [isNarrowView, setIsNarrowView] = useState(false);
  const [collapsed, setCollapsed] = useState(isNarrowView);

  const { syncStatus, isLoadingSyncStatus, refetchSyncInfo } = useMenuSyncStatus(menuId);
  const { moveItemToNoCategory, moveItemsToCategory, assignNoCategoryItemsToCategory } = useMoveMenuItems(url);

  const menuMetaContext = useMemo(
    () => ({
      menuId,
      merchantId,
      venueId,
    }),
    [menuId, merchantId, venueId],
  );

  const menuLocaleContext = useMemo(
    () => ({
      country,
      primaryLanguage,
      timezone,
    }),
    [country, primaryLanguage, timezone],
  );

  useLayoutEffect(() => {
    if (!rootRef.current || !window.ResizeObserver) {
      // Ignore if `ResizeObserver` not available in the browser.
      return;
    }
    const resizeObserver = new ResizeObserver(entries => setIsNarrowView(entries[0]?.contentRect.width <= 960));
    resizeObserver.observe(rootRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useTelemetryViewEvent('menu-editor-view', {}, durationMs => menuEditorView({ durationMs }));

  useEffect(() => {
    if (isNarrowView) {
      setCollapsed(isNarrowView);
    }
  }, [isNarrowView]);

  if (!categories) {
    return null;
  }

  const showMenuNavigation = !matchPath(pathname, {
    path: [`${path}/menu-copy`, `${path}/restocking-rules`],
    exact: true,
  });
  const allowMenuCopyAccess =
    syncStatus &&
    menuPermissions.menuSync &&
    syncStatus.status !== 'push-in-progress' &&
    syncStatus.status !== 'pull-in-progress' &&
    syncStatus.status !== 'push-scheduled' &&
    syncStatus.status !== 'pull-scheduled';

  return (
    <MenuMetaContext.Provider value={menuMetaContext}>
      <MenuLocaleContext.Provider value={menuLocaleContext}>
        <MenuPermissionsContext.Provider value={menuPermissions}>
          <MenuFeaturesContext.Provider value={menuFeatures}>
            <MenuSyncContext.Provider value={{ syncStatus, isLoadingSyncStatus, refetchSyncInfo }}>
              {/* The main element ref is required by the sticky header and footer implementations in ItemV2 */}
              <MenuMainElementRefContext.Provider value={mainElementRef}>
                <ThemeConsumer>
                  {theme => (
                    <Root dir={theme.cbDirection} ref={rootRef}>
                      <MenuEditorDnDContext menuEditorRootUrl={url}>
                        {showMenuNavigation && (
                          <LeftNavigation>
                            <NavigationContainer collapsed={collapsed}>
                              <Navigation
                                hasMenuItemExportImportPermission={menuPermissions.itemExportImport}
                                language={primaryLanguage}
                                menuId={menuId}
                                woltcomUrl={woltcomUrl}
                              />
                            </NavigationContainer>
                            <CollapseButton collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
                              <CollapseIcon direction={theme.cbDirection} collapsed={collapsed} />
                            </CollapseButton>
                          </LeftNavigation>
                        )}
                        {/* Pass a key prop to each <Route> and render <Main> inside each <Route>
                      to ensure that scroll position resets when route changes. */}
                        <Switch>
                          <Route exact path={path} key="all-items">
                            <Main ref={mainElementRef}>
                              <AllItems
                                menuId={menuId}
                                showWoltSpecials={menuPermissions.woltSpecials}
                                venueSlug={venueSlug}
                                country={country}
                                refetchMenu={refetchMenu}
                                onAssignNoCategoryItemsToCategory={assignNoCategoryItemsToCategory}
                                onMoveItemsToCategory={moveItemsToCategory}
                                onMoveItemToNoCategory={moveItemToNoCategory}
                              />
                            </Main>
                          </Route>
                          <Route exact path={`${path}/options`} key="options">
                            <Main ref={mainElementRef}>
                              <Options />
                            </Main>
                          </Route>
                          <Route exact path={`${path}/all-disabled`} key="all-disabled">
                            <Main ref={mainElementRef}>
                              <AllDisabled
                                menuEditorRootUrl={url}
                                showWoltSpecials={menuPermissions.woltSpecials}
                                onAssignNoCategoryItemsToCategory={assignNoCategoryItemsToCategory}
                                onMoveItemsToCategory={moveItemsToCategory}
                                onMoveItemToNoCategory={moveItemToNoCategory}
                              />
                            </Main>
                          </Route>
                          <Route exact path={`${path}/no-category`} key="no-category">
                            <Main ref={mainElementRef}>
                              <ItemsWithNoCategory
                                menuEditorRootUrl={url}
                                onAssignNoCategoryItemsToCategory={assignNoCategoryItemsToCategory}
                              />
                            </Main>
                          </Route>
                          <Route exact path={`${path}/add-categories`} key="no-category">
                            <Main ref={mainElementRef}>
                              <CreateCategoryFormContainer menuEditorRootUrl={url} />
                            </Main>
                          </Route>
                          {menuFeatures.restockingRulesEnabled && (
                            <Route exact path={`${path}/restocking-rules`} key="menu-restocking-rules">
                              <Main ref={mainElementRef}>
                                <RestockingRulesPage
                                  menuId={menuId}
                                  venueSlug={venueSlug}
                                  timezone={timezone}
                                  venueId={venueId}
                                  categories={categories}
                                  primaryLanguage={primaryLanguage}
                                />
                              </Main>
                            </Route>
                          )}
                          {allowMenuCopyAccess ? (
                            <Route exact path={`${path}/menu-copy`} key="menu-copy">
                              <Main ref={mainElementRef}>
                                <MenuSyncPage venueSlug={venueSlug} />
                              </Main>
                            </Route>
                          ) : (
                            <Redirect from={`${path}/menu-copy`} to={path} />
                          )}
                          {categories.map(({ id: categoryId }) => (
                            <Route exact path={`${path}/${categoryId}`} key={categoryId}>
                              <Main ref={mainElementRef}>
                                <CategoryItems
                                  categoryId={categoryId}
                                  menuEditorRootUrl={url}
                                  menuId={menuId}
                                  onMoveItemsToCategory={moveItemsToCategory}
                                  onMoveItemToNoCategory={moveItemToNoCategory}
                                />
                              </Main>
                            </Route>
                          ))}
                          {categories.flatMap(({ id: categoryId, child_categories }) =>
                            child_categories?.map(({ id: childCategoryId }) => (
                              <Route
                                exact
                                path={`${path}/${categoryId}/subcategories/${childCategoryId}`}
                                key={categoryId + childCategoryId}
                              >
                                <Main ref={mainElementRef}>
                                  <SubCategoryItems
                                    categoryId={childCategoryId}
                                    menuEditorRootUrl={url}
                                    menuId={menuId}
                                    onMoveItemsToCategory={moveItemsToCategory}
                                    onMoveItemToNoCategory={moveItemToNoCategory}
                                  />
                                </Main>
                              </Route>
                            )),
                          )}

                          <Route path={`${path}/*`} key="not-found">
                            <div>
                              <FormattedMessage id="menu.categoryNotFound" />
                            </div>
                          </Route>
                        </Switch>
                      </MenuEditorDnDContext>
                    </Root>
                  )}
                </ThemeConsumer>
              </MenuMainElementRefContext.Provider>
            </MenuSyncContext.Provider>
          </MenuFeaturesContext.Provider>
        </MenuPermissionsContext.Provider>
      </MenuLocaleContext.Provider>
    </MenuMetaContext.Provider>
  );
};

export default MenuEditor;
